import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout, { toast } from './IndustrialLayout';
import { makeSimpleRing } from '../../utils/polygon';

// ── Cleaning Zones ────────────────────────────────────────────────────────────
// Draw the floor that housekeeping is expected to clean. The backend's
// floor_cleaning detector then watches people standing on that floor during
// housekeeping hours, works out who is actually cleaning (bent over or working the
// arms, moving slowly), and records each session with its floor coverage and proof
// photos — the Cleaning Report.
//
// Draw the FLOOR, not the people. Whether someone is on a zone is decided by the
// point under their feet (bottom-centre of the person box), and the live check
// draws that point so it is never a guess. Keep turnstiles, walls and furniture
// outside the polygon — feet are hidden there, so coverage would read low.

const DEF = {
  zones: [],
  work_start: '07:00',
  work_end: '11:00',
  skip_outside_hours: true,
  uniform_hue: null,
  require_uniform: false,
  require_mop: false,
  min_session_secs: 120,
  merge_gap_secs: 300,
  lean_angle: 20,
  conf: 0.35,
  imgsz: 640,
};

// The uniform is stored as an OpenCV hue (0–179) — what the detector compares.
const hexToHue = (hex) => {
  const n = parseInt((hex || '#000000').slice(1), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (!d) return 0;
  let h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h = Math.round(h * 60); if (h < 0) h += 360;
  return Math.round(h / 2);
};
const hueToCss = (hue) => `hsl(${hue * 2}, 70%, 45%)`;

export default function IndustrialCleaningZonesPage() {
  const [cameras, setCameras] = useState([]);
  const [scope, setScope]     = useState(null);
  const [cfg, setCfg]         = useState(DEF);
  const [enabled, setEnabled] = useState([]);
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [active, setActive]   = useState(-1);     // zone highlighted on the canvas (hover)
  // Which zone the next "Finish zone" REPLACES, or -1 to add a new one — kept apart
  // from `active` for the same reason as on Seat Zones.
  const [redrawIdx, setRedrawIdx] = useState(-1);
  const [live, setLive]       = useState(false);  // poll the server-annotated frame
  const [imgUrl, setImgUrl]   = useState(null);
  const [imgErr, setImgErr]   = useState(false);
  const [pts, setPts]         = useState([]);     // in-progress normalized points

  const canvasRef = useRef(null);
  const imgRef    = useRef(null);
  const nav       = useNavigate();

  // Reaching this page already required the `industrial-cleaning` menu, which is
  // admin-only by default (absent from _OPERATIONAL_MENUS) — same as Seat Zones.
  const camName = cameras.find(c => c.idx === scope)?.name || (scope != null ? `Camera ${scope}` : '');
  const zones   = useMemo(() => cfg.zones || [], [cfg.zones]);

  // ── load ────────────────────────────────────────────────────────────────────
  const selectCamera = useCallback((idx, camList) => {
    const cams = camList || cameras;
    const cam  = cams.find(c => c.idx === idx);
    const saved = cam?.detector_config?.floor_cleaning;
    setScope(idx);
    setEnabled([...(cam?.detectors || [])]);
    setCfg({ ...DEF, ...(saved || {}), zones: (saved?.zones || []).map(z => ({ ...z })) });
    setPts([]); setActive(-1); setRedrawIdx(-1); setDirty(false); setImgErr(false);
    apiImageUrl(`/cameras/${idx}/frame`).then(u => setImgUrl(u)).catch(() => setImgUrl(null));
  }, [cameras]);

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => {
      const list = d.cameras || [];
      setCameras(list);
      if (list.length) selectCamera(list[0].idx, list);
    }).catch(() => {});
  }, []);  // eslint-disable-line

  // Live annotated frame — the server draws the zones, the person boxes and the
  // feet point that decides whether someone is on a zone.
  useEffect(() => {
    if (!live || scope == null) return;
    let alive = true;
    const tick = () => apiImageUrl(`/ui/cameras/${scope}/annotated?mode=cleaning&t=${Date.now()}`).then(u => {
      if (alive) { setImgUrl(u); setImgErr(false); }
    }).catch(() => {});
    tick(); const iv = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [live, scope]);

  // ── canvas ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const cv = canvasRef.current, im = imgRef.current;
    if (!cv || !im || !im.complete) return;
    const W = cv.width, H = cv.height, ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const dot = (p, color) => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(p[0] * W, p[1] * H, 6, 0, 7); ctx.fill(); };

    zones.forEach((z, i) => {
      const poly = z.polygon || [];
      if (poly.length < 3) return;
      if (i === redrawIdx) {
        // Being replaced: a faint dashed outline to trace against, not a shape
        // that looks like it is still there.
        ctx.save();
        ctx.setLineDash([6, 5]); ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.lineWidth = 1.5;
        ctx.beginPath();
        poly.forEach((p, j) => j ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
        ctx.closePath(); ctx.stroke();
        ctx.restore();
        return;
      }
      const on = i === active;
      ctx.strokeStyle = on ? '#22c55e' : '#10b981';
      ctx.fillStyle   = on ? 'rgba(34,197,94,0.24)' : 'rgba(16,185,129,0.16)';
      ctx.lineWidth   = on ? 3 : 2;
      ctx.beginPath();
      poly.forEach((p, j) => j ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.closePath(); ctx.fill(); ctx.stroke();
      if (on) poly.forEach(p => dot(p, '#22c55e'));
      ctx.fillStyle = '#86efac';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText(`🧹 ${z.name || `Floor ${i + 1}`}`, poly[0][0] * W + 4, poly[0][1] * H - 5);
    });

    if (pts.length) {
      ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 2;
      ctx.beginPath();
      pts.forEach((p, i) => i ? ctx.lineTo(p[0] * W, p[1] * H) : ctx.moveTo(p[0] * W, p[1] * H));
      ctx.stroke();
      if (pts.length >= 3) {
        ctx.save();
        ctx.setLineDash([5, 4]); ctx.strokeStyle = '#f59e0b'; ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(pts[pts.length - 1][0] * W, pts[pts.length - 1][1] * H);
        ctx.lineTo(pts[0][0] * W, pts[0][1] * H);
        ctx.stroke();
        ctx.restore();
      }
      pts.forEach((p, i) => {
        dot(p, '#f59e0b');
        ctx.fillStyle = '#1f2937'; ctx.font = 'bold 10px sans-serif';
        ctx.fillText(String(i + 1), p[0] * W - 3, p[1] * H + 3.5);
      });
    }
  }, [imgUrl, zones, pts, active, redrawIdx]);

  const onCanvasClick = (e) => {
    const cv = canvasRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const p = [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height];
    setPts(prev => [...prev, p]);
  };

  // ── zone edits ──────────────────────────────────────────────────────────────
  const patch = (k, v) => { setCfg(c => ({ ...c, [k]: v })); setDirty(true); };
  const patchZone = (i, k, v) => {
    setCfg(c => ({ ...c, zones: (c.zones || []).map((z, j) => j === i ? { ...z, [k]: v } : z) }));
    setDirty(true);
  };

  const finishZone = () => {
    if (pts.length < 3) { toast('Click at least 3 corners of the floor.', 'err'); return; }
    const { poly, fixed } = makeSimpleRing(pts);
    if (fixed) toast('Those corners crossed over — reordered them into a clean shape.', 'warn');
    if (redrawIdx >= 0 && zones[redrawIdx]) {
      patchZone(redrawIdx, 'polygon', poly);
      setPts([]); setRedrawIdx(-1);
      if (!fixed) toast(`“${zones[redrawIdx].name}” redrawn.`, 'ok');
      return;
    }
    const name = zones.length ? `Floor ${zones.length + 1}` : 'Reception floor';
    setCfg(c => ({ ...c, zones: [...(c.zones || []), { name, polygon: poly }] }));
    setActive(zones.length);
    setPts([]); setDirty(true);
    if (!fixed) toast(`${name} added — rename it if you like, then Save.`, 'ok');
  };

  const removeZone = (i) => {
    if (!window.confirm(`Remove “${zones[i]?.name || `Floor ${i + 1}`}”? Sessions already recorded are kept.`)) return;
    setCfg(c => ({ ...c, zones: (c.zones || []).filter((_, j) => j !== i) }));
    setActive(-1); setRedrawIdx(-1); setDirty(true);
  };

  const save = async () => {
    if (scope == null) return;
    let next = zones;
    if (pts.length >= 3) {          // auto-commit an in-progress polygon
      next = [...zones, { name: zones.length ? `Floor ${zones.length + 1}` : 'Reception floor',
                          polygon: makeSimpleRing(pts).poly }];
    }
    if (!next.length) { toast('Draw the floor zone first.', 'err'); return; }
    const dupe = next.map(z => (z.name || '').trim().toLowerCase())
      .find((n, i, a) => n && a.indexOf(n) !== i);
    if (dupe) { toast(`Two zones are both named “${dupe}” — names identify the report rows.`, 'err'); return; }

    setSaving(true);
    try {
      const dets = enabled.includes('floor_cleaning') ? enabled : [...enabled, 'floor_cleaning'];
      const body = { detectors: dets, detector_config: { floor_cleaning: { ...cfg, zones: next } } };
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error();
      setCfg(c => ({ ...c, zones: next })); setPts([]); setEnabled(dets); setDirty(false);
      toast(`Saved — cleaning measured on ${next.length} zone${next.length === 1 ? '' : 's'} of ${camName}.`, 'ok');
    } catch { toast('Save failed.', 'err'); }
    finally { setSaving(false); }
  };

  const disable = async () => {
    if (!window.confirm(`Stop measuring cleaning on ${camName}? The drawn zones are kept.`)) return;
    const dets = enabled.filter(d => d !== 'floor_cleaning');
    try {
      const r = await apiFetch(`/cameras/${scope}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ detectors: dets }),
      });
      if (!r.ok) throw new Error();
      setEnabled(dets);
      toast('Cleaning report switched off for this camera.', 'warn');
    } catch { toast('Could not switch it off.', 'err'); }
  };

  const on = enabled.includes('floor_cleaning');
  const hasUniform = cfg.uniform_hue != null && cfg.uniform_hue !== '';

  return (
    <IndustrialLayout eyebrow="Cleaning" title="Cleaning Zones"
      subtitle={`${camName.toUpperCase()} · ${zones.length} ZONE${zones.length === 1 ? '' : 'S'}${on ? ' · MEASURING' : ''}`}>

      <div className="ind-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span className="ind-eye">Camera</span>
          <select value={scope ?? ''} style={sel}
            onChange={e => { if (dirty && !window.confirm('Discard unsaved changes?')) return; selectCamera(Number(e.target.value)); }}>
            {cameras.map(c => <option key={c.idx} value={c.idx}>🎥 {c.name || `Camera ${c.idx}`}</option>)}
          </select>
          <button className={`ind-pill${live ? ' on' : ''}`} onClick={() => setLive(l => !l)}>
            {live ? '⏸ Pause live' : '▶ Live check'}
          </button>
          <button className="ind-pill" onClick={() => { setLive(false); setImgErr(false); apiImageUrl(`/cameras/${scope}/frame`).then(setImgUrl).catch(() => setImgErr(true)); }}>
            ↻ Refresh frame
          </button>
          <span style={{ flex: 1 }} />
          <span className={`ind-pill${on ? ' on' : ''}`} style={{ cursor: 'default' }}>
            {on ? '● Measuring' : '○ Not enabled'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* LEFT — the frame + drawing surface */}
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <div style={{ position: 'relative', width: '100%', minHeight: 280, borderRadius: 8, overflow: 'hidden',
              border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', display: 'grid', placeItems: 'center' }}>
              {imgUrl && !imgErr
                ? <img ref={imgRef} src={imgUrl} alt="" onError={() => setImgErr(true)}
                    onLoad={() => { const cv = canvasRef.current, im = imgRef.current; if (cv && im) { cv.width = im.clientWidth; cv.height = im.clientHeight; setPts(p => [...p]); } }}
                    style={{ width: '100%', display: 'block' }} />
                : <div className="ind-empty" style={{ padding: 24, textAlign: 'center', maxWidth: 420 }}>
                    {imgErr
                      ? <>📷 Couldn’t load a frame from <b>{camName}</b>.<br />The camera may be offline. Try <b>↻ Refresh frame</b>.</>
                      : <>📷 Pick a camera to load a still to draw on.</>}
                  </div>}
              {imgUrl && !imgErr &&
                <canvas ref={canvasRef} onClick={onCanvasClick}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', cursor: 'crosshair' }} />}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="ind-pill on" onClick={finishZone} disabled={pts.length < 3}>
                ✓ Finish zone ({pts.length} pts)
              </button>
              {pts.length > 0 && <button className="ind-pill" onClick={() => setPts(p => p.slice(0, -1))}>↶ Undo point</button>}
              {pts.length > 0 && <button className="ind-pill" onClick={() => setPts([])}>Clear points</button>}
              <span style={{ fontSize: 12, color: 'var(--ind-tx3)' }}>
                {redrawIdx >= 0 && zones[redrawIdx]
                  ? `Redrawing “${zones[redrawIdx].name}” — the dashed outline is the old shape. Click new corners, then Finish zone (Cancel keeps the old one).`
                  : zones.length
                    ? 'Click the corners of another floor area, or Save.'
                    : 'Click the corners of the floor to be cleaned — leave turnstiles, walls and furniture outside — then Finish zone.'}
              </span>
            </div>

            {live && (
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 8, lineHeight: 1.6 }}>
                The orange dot under each person is the point that decides whether they are on a zone.
                A zone turns orange while someone stands on it and green once they are counted as
                cleaning — that takes about half a minute of cleaning, so walking across never does.
              </p>
            )}
          </div>

          {/* RIGHT — zone list + settings */}
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <span className="ind-eye">Floor zones on this camera</span>
            <div style={{ border: '1px solid var(--ind-line)', borderRadius: 8, marginTop: 8, overflow: 'hidden' }}>
              {zones.length === 0 && <div className="ind-empty" style={{ padding: 18, fontSize: 12 }}>No floor zone drawn yet.</div>}
              {zones.map((z, i) => (
                <div key={i} onMouseEnter={() => setActive(i)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                    borderTop: i ? '1px solid var(--ind-line)' : 'none',
                    background: i === active ? 'rgba(34,197,94,0.08)' : 'transparent' }}>
                  <span style={{ fontSize: 14 }}>🧹</span>
                  <input value={z.name || ''}
                    onChange={e => patchZone(i, 'name', e.target.value)}
                    placeholder={`Floor ${i + 1}`}
                    style={{ ...sel, flex: 1, minWidth: 0 }} />
                  <span style={{ fontSize: 10, color: 'var(--ind-tx3)', fontFamily: 'var(--ind-mono)' }}>
                    {(z.polygon || []).length}p
                  </span>
                  <button className={`ind-pill${redrawIdx === i ? ' on' : ''}`} style={{ padding: '2px 8px' }}
                    onClick={() => { setActive(i); setRedrawIdx(redrawIdx === i ? -1 : i); setPts([]); }}>
                    {redrawIdx === i ? 'Cancel' : 'Redraw'}
                  </button>
                  <button className="ind-pill" style={{ padding: '2px 8px' }}
                    onClick={() => removeZone(i)}>✕</button>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 6, lineHeight: 1.6 }}>
              The zone name is the report row. One zone per area you want reported separately —
              e.g. the reception floor and the walkway.
            </p>

            <div style={{ marginTop: 16 }}>
              <span className="ind-eye">Housekeeping hours</span>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="time" value={cfg.work_start || ''}
                  onChange={e => patch('work_start', e.target.value)} style={sel} />
                <span style={{ color: 'var(--ind-tx3)', fontSize: 12 }}>to</span>
                <input type="time" value={cfg.work_end || ''}
                  onChange={e => patch('work_end', e.target.value)} style={sel} />
              </div>
              <label style={lbl}>
                <input type="checkbox" checked={!!cfg.skip_outside_hours}
                  onChange={e => patch('skip_outside_hours', e.target.checked)} />
                Only measure in these hours (±30 min)
              </label>
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '4px 0 0', lineHeight: 1.6 }}>
                Cleaning is watched closely (every second while someone is on the floor), so limiting it
                to housekeeping hours saves a lot of CPU. Untick to measure all day.
              </p>
            </div>

            <fieldset disabled={!!cfg.require_mop}
              style={{ marginTop: 16, border: 'none', padding: 0, minWidth: 0, opacity: cfg.require_mop ? 0.45 : 1 }}>
              <span className="ind-eye">Housekeeping uniform (optional)</span>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ ...lbl, marginTop: 0 }}>
                  <input type="checkbox" checked={hasUniform}
                    onChange={e => patch('uniform_hue', e.target.checked ? 140 : null)} />
                  Staff wear a uniform colour
                </label>
                {hasUniform && <>
                  <input type="color" value={hslHex(cfg.uniform_hue)}
                    onChange={e => patch('uniform_hue', hexToHue(e.target.value))}
                    style={{ width: 40, height: 28, border: 'none', background: 'none', padding: 0 }} />
                  <span style={{ width: 16, height: 16, borderRadius: 4, background: hueToCss(cfg.uniform_hue),
                    display: 'inline-block' }} />
                </>}
              </div>
              {hasUniform && (
                <label style={lbl}>
                  <input type="checkbox" checked={!!cfg.require_uniform}
                    onChange={e => patch('require_uniform', e.target.checked)} />
                  Only people in this uniform can count as cleaning
                </label>
              )}
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '4px 0 0', lineHeight: 1.6 }}>
                {cfg.require_mop ? MOP_OFF : <>Pick the main colour of the shirt (e.g. purple). A match makes the reading more confident;
                requiring it stops a visitor who bends down from ever counting.</>}
              </p>
            </fieldset>

            <div style={{ marginTop: 16 }}>
              <span className="ind-eye">Mop</span>
              <label style={lbl}>
                <input type="checkbox" checked={!!cfg.require_mop}
                  onChange={e => patch('require_mop', e.target.checked)} />
                Only someone holding a mop counts as cleaning
              </label>
              <p style={{ fontSize: 11, color: 'var(--ind-tx3)', margin: '4px 0 0', lineHeight: 1.6 }}>
                For busy floors where people walking past were counted, or where staff mop standing
                upright. Posture is then ignored. Needs the mop model on the box (download_models.py).
              </p>
            </div>

            <details style={{ marginTop: 16 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ind-tx2)' }}>Fine tuning</summary>
              <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                <Field label="Shortest session" hint="Cleaning shorter than this is dropped — someone wiping one spot is not a floor clean.">
                  <input type="number" min={0} max={30} value={Math.round((cfg.min_session_secs ?? 120) / 60)}
                    onChange={e => patch('min_session_secs', Math.max(0, Number(e.target.value) || 0) * 60)} style={{ ...sel, width: 80 }} /> min
                </Field>
                <Field label="Same session across breaks under" hint="A pause to refill the bucket keeps one session. The paused time is not counted as cleaning.">
                  <input type="number" min={0} max={60} value={Math.round((cfg.merge_gap_secs ?? 300) / 60)}
                    onChange={e => patch('merge_gap_secs', Math.max(0, Number(e.target.value) || 0) * 60)} style={{ ...sel, width: 80 }} /> min
                </Field>
                <Field label="Bent over from" off={cfg.require_mop && MOP_OFF} hint="Torso angle off vertical that counts as cleaning posture. Lower catches upright mopping, but also people leaning on the desk.">
                  <input type="number" min={5} max={60} value={cfg.lean_angle}
                    onChange={e => patch('lean_angle', Number(e.target.value) || 20)} style={{ ...sel, width: 80 }} /> °
                </Field>
                <Field label="Detection confidence" hint="Lower finds people further away but risks false positives.">
                  <input type="number" min={0.05} max={0.9} step={0.05} value={cfg.conf}
                    onChange={e => patch('conf', Number(e.target.value) || 0.35)} style={{ ...sel, width: 80 }} />
                </Field>
              </div>
            </details>

            <div style={{ display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
              <button className="ind-pill on" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : dirty ? '💾 Save zones' : '💾 Save'}
              </button>
              {on && <button className="ind-pill" onClick={disable}>Switch off</button>}
              <button className="ind-pill" onClick={() => nav('/industrial/cleaning/report')}>
                📊 Open report
              </button>
            </div>
          </div>
        </div>

        <p style={{ fontSize: 11, color: 'var(--ind-tx3)', marginTop: 16, lineHeight: 1.7, maxWidth: 780 }}>
          <b>How the measurement works.</b> {cfg.require_mop
            ? <>A person on the floor counts as cleaning while a mop is seen in their hands — at least twice a
              minute, checked every few seconds. Posture and uniform are not used.</>
            : <>A person on the floor counts as cleaning when, for about half a
          minute, they move slowly and are either bent over or working their arms back and forth.</>} Coverage is
          the share of the zone their feet came near while cleaning. The Cleaning Report raises no alerts and
          records no video — it keeps each session’s time, coverage and three photos.
        </p>
      </div>
    </IndustrialLayout>
  );
}

// hsl(h, 70%, 45%) as #rrggbb, for the colour input's value.
function hslHex(hue) {
  const h = (Number(hue) || 0) * 2, s = 0.7, l = 0.45;
  const k = n => (n + h / 30) % 12, a = s * Math.min(l, 1 - l);
  const f = n => Math.round(255 * (l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1))));
  return `#${[f(0), f(8), f(4)].map(x => x.toString(16).padStart(2, '0')).join('')}`;
}

// `off` = the setting does nothing in the current mode: greyed and locked, with why.
function Field({ label, hint, off, children }) {
  return (
    <fieldset disabled={!!off} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0, opacity: off ? 0.45 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ind-tx2)' }}>
        <span style={{ minWidth: 150 }}>{label}</span>{children}
      </div>
      {(off || hint) && <div style={{ fontSize: 10.5, color: 'var(--ind-tx3)', marginTop: 3, lineHeight: 1.5 }}>{off || hint}</div>}
    </fieldset>
  );
}

const MOP_OFF = 'Not used while “Only someone holding a mop” is on.';

const sel = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 12 };
const lbl = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ind-tx2)', marginTop: 8 };
